import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { indexersApi } from "@/api/indexers";
import {
  savedSearchesApi,
  type CreateSavedSearchRequest,
  type SavedSearch,
} from "@/api/savedSearches";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  Checkbox,
  Field,
  NumberInput,
  TextInput,
} from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * Newznab category presets surfaced in the picker. Mirrors the ones
 * on `/search` so users have the same vocabulary in both places.
 */
const CATEGORY_PRESETS: { value: number; label: string }[] = [
  { value: 2000, label: "Movies" },
  { value: 5000, label: "TV" },
  { value: 3000, label: "Music" },
  { value: 6000, label: "Books" },
  { value: 7000, label: "PC" },
  { value: 8000, label: "Other" },
];

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: SavedSearch };

export function SavedSearches() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });
  const [runningId, setRunningId] = useState<number | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["saved-searches"],
    queryFn: savedSearchesApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => savedSearchesApi.remove(id),
    onSuccess: () => {
      toast("Saved search removed", "success");
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const toggleEnabled = async (row: SavedSearch) => {
    try {
      await savedSearchesApi.update(row.id, { enabled: !row.enabled });
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const toggleAutoGrab = async (row: SavedSearch) => {
    try {
      await savedSearchesApi.update(row.id, { auto_grab: !row.auto_grab });
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const runNow = async (row: SavedSearch) => {
    setRunningId(row.id);
    try {
      const outcome = await savedSearchesApi.run(row.id);
      toast(
        `Ran "${row.name}": ${outcome.fresh_hits} new of ${outcome.total_hits} hits` +
          (outcome.grab_dispatched ? " — auto-grab dispatched" : ""),
        outcome.fresh_hits > 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
      qc.invalidateQueries({ queryKey: ["saved-search", row.id] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Run failed", "error");
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Saved Searches"
        description="Recurring indexer queries — fired on a cron schedule, with optional auto-grab on the top hit."
        actions={
          <Button onClick={() => setEditing({ mode: "create" })}>
            New saved search
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title="No saved searches"
          description="Persist a query you'd like to run on a recurring schedule. Useful when the release isn't catalogued yet."
          action={
            <Button onClick={() => setEditing({ mode: "create" })}>
              New saved search
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Query</th>
                <th className="px-4 py-2 font-medium">Schedule</th>
                <th className="px-4 py-2 font-medium">Last run</th>
                <th className="px-4 py-2 font-medium">Hits</th>
                <th className="px-4 py-2 font-medium">Enabled</th>
                <th className="px-4 py-2 font-medium">Auto-grab</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-4 py-2 font-medium">
                    <Link
                      to="/search/saved/$id"
                      params={{ id: String(row.id) }}
                      className="hover:text-accent"
                    >
                      {row.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/70">
                    {row.query}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/70">
                    {row.schedule_cron ?? (
                      <span className="text-foreground/40">manual</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.last_run_at
                      ? new Date(row.last_run_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.last_hit_count ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <Checkbox
                      checked={row.enabled}
                      onChange={() => toggleEnabled(row)}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Checkbox
                      checked={row.auto_grab}
                      onChange={() => toggleAutoGrab(row)}
                    />
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={runningId === row.id}
                      onClick={() => runNow(row)}
                    >
                      Run now
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Remove saved search "${row.name}"?`)) {
                          removeMut.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SavedSearchModal
        editing={editing}
        onClose={() => setEditing({ mode: "closed" })}
      />
    </div>
  );
}

interface FormState {
  name: string;
  query: string;
  selectedIndexers: Set<number>;
  selectedCategories: Set<number>;
  schedule_cron: string;
  max_size_bytes: string;
  min_seeders: string;
  enabled: boolean;
  auto_grab: boolean;
}

const blankForm = (): FormState => ({
  name: "",
  query: "",
  selectedIndexers: new Set(),
  selectedCategories: new Set(),
  schedule_cron: "",
  max_size_bytes: "",
  min_seeders: "",
  enabled: true,
  auto_grab: false,
});

const formFromRow = (row: SavedSearch): FormState => {
  let indexers: number[] = [];
  let categories: number[] = [];
  if (row.indexer_ids_json) {
    try {
      const parsed = JSON.parse(row.indexer_ids_json);
      if (Array.isArray(parsed))
        indexers = parsed.filter((n) => typeof n === "number");
    } catch {
      /* malformed — leave empty */
    }
  }
  if (row.categories_json) {
    try {
      const parsed = JSON.parse(row.categories_json);
      if (Array.isArray(parsed))
        categories = parsed.filter((n) => typeof n === "number");
    } catch {
      /* malformed */
    }
  }
  return {
    name: row.name,
    query: row.query,
    selectedIndexers: new Set(indexers),
    selectedCategories: new Set(categories),
    schedule_cron: row.schedule_cron ?? "",
    max_size_bytes: row.max_size_bytes != null ? String(row.max_size_bytes) : "",
    min_seeders: row.min_seeders != null ? String(row.min_seeders) : "",
    enabled: row.enabled,
    auto_grab: row.auto_grab,
  };
};

const toBody = (f: FormState): CreateSavedSearchRequest => {
  const max = f.max_size_bytes.trim();
  const minS = f.min_seeders.trim();
  return {
    name: f.name.trim(),
    query: f.query.trim(),
    indexer_ids:
      f.selectedIndexers.size > 0 ? Array.from(f.selectedIndexers) : undefined,
    categories:
      f.selectedCategories.size > 0
        ? Array.from(f.selectedCategories)
        : undefined,
    schedule_cron: f.schedule_cron.trim() ? f.schedule_cron.trim() : null,
    max_size_bytes: max ? Number(max) : null,
    min_seeders: minS ? Number(minS) : null,
    enabled: f.enabled,
    auto_grab: f.auto_grab,
  };
};

function SavedSearchModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);

  const indexers = useQuery({
    queryKey: ["indexers"],
    queryFn: indexersApi.list,
  });
  const enabledIndexers = useMemo(
    () => (indexers.data ?? []).filter((i) => i.enabled),
    [indexers.data],
  );

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = toBody(form);
      if (!body.name) {
        toast("Name is required", "error");
        return;
      }
      if (!body.query) {
        toast("Query is required", "error");
        return;
      }
      if (isEdit && editingRow) {
        await savedSearchesApi.update(editingRow.id, {
          ...body,
          // PATCH semantics: send the value verbatim (incl. nulls).
          indexer_ids: body.indexer_ids ?? null,
          categories: body.categories ?? null,
        });
        toast("Saved search updated", "success");
      } else {
        await savedSearchesApi.create(body);
        toast("Saved search created", "success");
      }
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleIndexer = (id: number) => {
    setForm((f) => {
      const s = new Set(f.selectedIndexers);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return { ...f, selectedIndexers: s };
    });
  };

  const toggleCategory = (id: number) => {
    setForm((f) => {
      const s = new Set(f.selectedCategories);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return { ...f, selectedCategories: s };
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? `Edit ${editingRow?.name ?? "saved search"}`
          : "New saved search"
      }
      width="lg"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name">
            <TextInput
              required
              value={form.name}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="e.g. Friday Childers Sweep"
            />
          </Field>
          <Field label="Cron schedule" hint="5-field POSIX (min hour dom mon dow). Empty = manual.">
            <TextInput
              value={form.schedule_cron}
              onChange={(e) =>
                setForm((f) => ({ ...f, schedule_cron: e.target.value }))
              }
              placeholder="0 18 * * 5"
            />
          </Field>
        </div>

        <Field label="Query">
          <TextInput
            required
            value={form.query}
            onChange={(e) =>
              setForm((f) => ({ ...f, query: e.target.value }))
            }
            placeholder="Tyler Childers Snipe Hunter"
          />
        </Field>

        <Field
          label="Indexers"
          hint="Empty = all enabled indexers will be queried."
        >
          <div className="flex flex-wrap gap-2">
            {enabledIndexers.length === 0 ? (
              <span className="text-xs text-foreground/40">
                no enabled indexers
              </span>
            ) : (
              enabledIndexers.map((i) => {
                const active = form.selectedIndexers.has(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => toggleIndexer(i.id)}
                    className={
                      "rounded-md border px-2.5 py-1 text-xs " +
                      (active
                        ? "border-accent/60 bg-accent/15 text-foreground"
                        : "border-line text-foreground/65 hover:border-line-strong")
                    }
                  >
                    {i.name}
                  </button>
                );
              })
            )}
          </div>
        </Field>

        <Field label="Categories" hint="Empty = no category restriction.">
          <div className="flex flex-wrap gap-2">
            {CATEGORY_PRESETS.map((c) => {
              const active = form.selectedCategories.has(c.value);
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => toggleCategory(c.value)}
                  className={
                    "rounded-md border px-2.5 py-1 text-xs " +
                    (active
                      ? "border-accent/60 bg-accent/15 text-foreground"
                      : "border-line text-foreground/65 hover:border-line-strong")
                  }
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Max size (bytes)" hint="Optional cap.">
            <NumberInput
              min={0}
              value={form.max_size_bytes}
              onChange={(e) =>
                setForm((f) => ({ ...f, max_size_bytes: e.target.value }))
              }
              placeholder="e.g. 10737418240"
            />
          </Field>
          <Field label="Min seeders" hint="Torrents only.">
            <NumberInput
              min={0}
              value={form.min_seeders}
              onChange={(e) =>
                setForm((f) => ({ ...f, min_seeders: e.target.value }))
              }
              placeholder="e.g. 5"
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
            />
            <span>Enabled</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.auto_grab}
              onChange={(e) =>
                setForm((f) => ({ ...f, auto_grab: e.target.checked }))
              }
            />
            <span>Auto-grab top fresh hit</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-line/60 pt-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
